import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { createClient } from "graphql-ws";

export interface IBandRouteMethods extends IPluginMethodMap {
  getBandRouteProps(args?: any): Promise<any>;
  subscribeToLiveBands(): void;
  onUpdate(callback: (data: any) => void): () => void;
  onConnectionChange(callback: (status: string) => void): () => void;
}

interface TruckLocation {
  band: "TRIBE" | "BLISS" | "HARTS" | "LOST";
  type: "01" | "02";
  latitude: number;
  longitude: number;
}

export class BandRouteService implements IAgentPlugin {
  readonly methods: IBandRouteMethods;

  private wsClient: any;
  private listeners: ((data: TruckLocation[]) => void)[] = [];
  private connectionListeners: ((status: string) => void)[] = [];
  private subscribed = false;
  private currentStatus: string = "CONNECTING";

  constructor() {
    this.methods = {
      getBandRouteProps: this.getBandRouteProps.bind(this),
      subscribeToLiveBands: this.subscribeToLiveBands.bind(this),
      onUpdate: this.onUpdate.bind(this),
      onConnectionChange: this.onConnectionChange.bind(this),
    };

    this.initializeWebSocket();
  }

  private initializeWebSocket() {
    this.wsClient = createClient({
      url: "https://gem-be.vlinderserver.online/core/wss/",
      retryAttempts: Infinity,
      keepAlive: 10000,
      on: {
        opened: () => {
          this.currentStatus = "CONNECTED";
          this.emitConnection("CONNECTED");
        },

        closed: () => {
          this.subscribed = false;
          this.currentStatus = "DISCONNECTED";
          this.emitConnection("DISCONNECTED");
        },

        connecting: () => {
          this.currentStatus = "CONNECTING";
          this.emitConnection("CONNECTING");
        },
      },
    });
  }

  public subscribeToLiveBands() {
    if (this.subscribed) return;
    this.subscribed = true;
    this.wsClient.subscribe(
      {
        query: `
          subscription {
            bandRouteUpdated
          }
        `,
      },
      {
        next: (result: any) => {
          console.log("WebSocket Data Received:", result);

          let raw = result?.data?.bandRouteUpdated;
          if (!raw) return;

          if (typeof raw === "string") {
            try {
              raw = JSON.parse(raw);
            } catch {
              console.error("Invalid JSON from socket");
              return;
            }
          }

          const formatted = this.formatSocketResponse(raw);
          console.log(formatted, "formatted data");
          this.emit(formatted);
        },
        error: (err: any) => {
          console.error("WebSocket Error:", err);
          this.emitConnection("ERROR");
        },
        complete: () => {
          console.log("WebSocket completed");
        },
      },
    );
  }

  private formatSocketResponse(raw: any): TruckLocation[] {
    const result: TruckLocation[] = [];

    if (!Array.isArray(raw)) return result;

    raw.forEach((bandItem: any) => {
      const rawName = bandItem?.bandShortName || bandItem?.name;
      if (!rawName) return;

      const band = this.normalizeBandName(rawName);

      if (bandItem?.l1?.coordinates) {
        result.push({
          band,
          type: "01",
          latitude: bandItem.l1.coordinates[1],
          longitude: bandItem.l1.coordinates[0],
        });
      }

      if (bandItem?.l2?.coordinates) {
        result.push({
          band,
          type: "02",
          latitude: bandItem.l2.coordinates[1],
          longitude: bandItem.l2.coordinates[0],
        });
      }
    });

    return result;
  }

  private normalizeBandName(name: string) {
    const upper = name.toUpperCase().trim();

    if (upper === "TRIBE") return "TRIBE";
    if (upper === "BLISS") return "BLISS";
    if (upper === "HARTS") return "HARTS";
    if (upper === "THE LOST TRIBE" || upper === "LOST TRIBE") return "LOST";
    if (
      upper === "MAD J'Ouvert" ||
      upper === "MAD J'OUVERT" ||
      upper === "MAD_JOUVERT" ||
      upper === "MAD"
    )
      return "MAD";

    return "TRIBE";
  }

  // public onUpdate(callback: (data: TruckLocation[]) => void) {
  //   this.listeners.push(callback);
  // }

  public onUpdate(callback: (data: TruckLocation[]) => void) {
    this.listeners.push(callback);

    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  private emit(data: TruckLocation[]) {
    this.listeners.forEach((cb) => cb(data));
  }

  public onConnectionChange(callback: (status: string) => void) {
    this.connectionListeners.push(callback);

    callback(this.currentStatus);

    return () => {
      this.connectionListeners = this.connectionListeners.filter(
        (cb) => cb !== callback,
      );
    };
  }

  private emitConnection(status: string) {
    this.connectionListeners.forEach((cb) => cb(status));
  }

  async getBandRouteProps(_: any) {
    return [];
  }
}
